import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { ClipboardDocumentIcon, CheckIcon, PrinterIcon } from '@heroicons/react/20/solid';
import {
  Modal,
  ModalFooter,
  ModalButton,
  Input,
  Textarea,
} from './ui';
import { printLotLabels } from '../utils/printLotLabels';

const compactInput =
  'w-full px-1.5 py-1 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums text-right';

const today = () => new Date().toISOString().slice(0, 10);

export default function POReceivingModal({ isOpen, onClose, po, lines, onReceived }) {
  const { user } = useAuth();
  const [packingSlipRef, setPackingSlipRef] = useState('');
  const [notes, setNotes] = useState('');
  const [receivedDate, setReceivedDate] = useState(today());
  const [receivingQtys, setReceivingQtys] = useState({});
  const [saving, setSaving] = useState(false);

  // After save: array of inserted receipts (with lot_numbers). When non-null, show confirmation step.
  const [confirmedReceipts, setConfirmedReceipts] = useState(null);
  const [copiedLot, setCopiedLot] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const defaults = {};
    lines.forEach((l) => {
      const ordered = Number(l.quantity || 0);
      const received = Number(l.received_quantity || 0);
      const outstanding = Math.max(0, ordered - received);
      defaults[l.id] = outstanding > 0 ? String(outstanding) : '';
    });
    setReceivingQtys(defaults);
    setPackingSlipRef('');
    setNotes('');
    setReceivedDate(today());
    setConfirmedReceipts(null);
    setCopiedLot(null);
  }, [isOpen, lines]);

  const totalReceiving = useMemo(
    () => Object.values(receivingQtys).reduce((sum, v) => sum + Number(v || 0), 0),
    [receivingQtys]
  );

  const handleSave = async () => {
    if (saving) return;
    const rows = lines
      .map((l) => {
        const qty = Number(receivingQtys[l.id] || 0);
        if (qty <= 0) return null;
        return {
          purchase_order_id: po.id,
          po_line_id: l.id,
          received_quantity: qty,
          received_date: receivedDate || today(),
          received_by: user?.id || null,
          packing_slip_ref: packingSlipRef.trim() || null,
          notes: notes.trim() || null,
        };
      })
      .filter(Boolean);

    if (rows.length === 0) {
      toast.error('Enter a quantity for at least one line.');
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('purchase_order_receipts')
        .insert(rows)
        .select('id, lot_number, po_line_id, received_quantity, received_date');
      if (error) throw error;
      // Map line info onto each receipt for the confirmation view
      const enriched = (data || []).map((r) => {
        const line = lines.find((l) => l.id === r.po_line_id);
        return { ...r, line };
      });
      setConfirmedReceipts(enriched);
      toast.success(`Received ${rows.length} line${rows.length === 1 ? '' : 's'}.`);
      onReceived?.();
    } catch (err) {
      console.error('Receive failed:', err);
      toast.error(err.message || 'Failed to record receipt.');
    } finally {
      setSaving(false);
    }
  };

  const copyLot = async (lot) => {
    try {
      await navigator.clipboard.writeText(lot);
      setCopiedLot(lot);
      setTimeout(() => setCopiedLot((cur) => (cur === lot ? null : cur)), 1500);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  const showConfirmation = !!confirmedReceipts;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showConfirmation ? 'Receipt logged' : `Receive against ${po?.po_number || 'PO'}`}
      size="xl"
    >
      {showConfirmation ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            {confirmedReceipts.length} lot{confirmedReceipts.length === 1 ? '' : 's'} created. Print
            a label for each piece of material as it goes to the rack:
          </p>

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Lot #</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Line / Description</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-24">Qty</th>
                  <th className="px-2 py-1.5 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {confirmedReceipts.map((r) => (
                  <tr key={r.id}>
                    <td className="px-2 py-1.5 text-xs font-mono text-slate-800">{r.lot_number}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-700">
                      <span className="text-slate-500">Line {r.line?.line_number}</span>
                      {r.line?.description ? ` · ${r.line.description}` : ''}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right text-slate-700 tabular-nums">
                      {Number(r.received_quantity)} {r.line?.uom || ''}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        onClick={() => copyLot(r.lot_number)}
                        className="p-1 text-slate-400 hover:text-slate-700"
                        title="Copy lot number"
                      >
                        {copiedLot === r.lot_number
                          ? <CheckIcon className="w-4 h-4 text-emerald-600" />
                          : <ClipboardDocumentIcon className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ModalFooter>
            <ModalButton
              variant="secondary"
              onClick={() => {
                const labels = (confirmedReceipts || []).map((r) => ({
                  lot_number: r.lot_number,
                  description: r.line?.description,
                  quantity: Number(r.received_quantity),
                  uom: r.line?.uom,
                  vendor_name: po?.suppliers?.name,
                  po_number: po?.po_number,
                  received_date: r.received_date,
                }));
                printLotLabels(labels);
              }}
            >
              <PrinterIcon className="w-4 h-4" /> Print labels
            </ModalButton>
            <ModalButton variant="primary" onClick={onClose}>
              Done
            </ModalButton>
          </ModalFooter>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Packing Slip Ref"
              value={packingSlipRef}
              onChange={(e) => setPackingSlipRef(e.target.value)}
              placeholder="optional"
            />
            <Input
              label="Received Date"
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
            />
          </div>

          <Textarea
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="optional"
          />

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-10">#</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Description</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-20">Ordered</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-20">Already Recv'd</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-20">Outstanding</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-28">Receiving Now</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-xs text-slate-400">
                      PO has no line items.
                    </td>
                  </tr>
                ) : (
                  lines.map((l) => {
                    const ordered = Number(l.quantity || 0);
                    const received = Number(l.received_quantity || 0);
                    const outstanding = Math.max(0, ordered - received);
                    const isComplete = outstanding === 0;
                    return (
                      <tr key={l.id} className={isComplete ? 'bg-emerald-50/30' : ''}>
                        <td className="px-2 py-1.5 text-xs text-slate-600">{l.line_number}</td>
                        <td className="px-2 py-1.5 text-xs text-slate-700">
                          {l.description || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-slate-700 text-right tabular-nums">
                          {ordered} {l.uom}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-slate-600 text-right tabular-nums">{received}</td>
                        <td className={`px-2 py-1.5 text-xs text-right tabular-nums ${isComplete ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {outstanding}
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={receivingQtys[l.id] ?? ''}
                            onChange={(e) =>
                              setReceivingQtys((q) => ({ ...q, [l.id]: e.target.value }))
                            }
                            disabled={isComplete}
                            placeholder={isComplete ? '—' : '0'}
                            className={`${compactInput} disabled:bg-slate-50 disabled:text-slate-400`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-slate-500">
            Total receiving this batch: <span className="font-medium text-slate-700 tabular-nums">{totalReceiving}</span>.
            A unique lot # is auto-generated per line.
          </div>

          <ModalFooter>
            <ModalButton variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </ModalButton>
            <ModalButton variant="primary" onClick={handleSave} loading={saving}>
              Receive
            </ModalButton>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
